import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircleOutline,
  StarOutline,
  ArrowBackOutlined,
  ElectricBoltOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import stripeService from '../services/stripeService';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isYearly, setIsYearly] = useState(true); // Par défaut annuel pour mettre en avant l'économie
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (tier: 'ESSENTIEL' | 'PREMIUM') => {
    setIsLoading(true);
    setError(null);

    try {
      const billingCycle = isYearly ? 'YEARLY' : 'MONTHLY';
      const checkoutUrl = await stripeService.createCheckoutSession(tier, billingCycle);

      // Rediriger vers Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      console.error('Erreur lors de la création de la session de paiement:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la session de paiement');
      setIsLoading(false);
    }
  };

  // Prix en euros HT
  const pricing = {
    ESSENTIEL: {
      monthly: 12.9,
      yearly: 129, // 10,75€/mois (économie de 17%)
    },
    PREMIUM: {
      monthly: 19.9,
      yearly: 199, // 16,58€/mois (économie de 17%)
    },
  };

  const essentielFeatures = [
    'Devis illimités avec PDF',
    'Factures illimitées avec PDF',
    'Gestion des clients',
    'Planning des chantiers',
    'Photos de chantier',
    'Calculateurs métiers',
    'Support standard',
  ];

  const premiumFeatures = [
    'Tout Essentiel +',
    '✍️ Signature électronique à distance',
    '💳 Paiement client par CB intégré',
    '📊 Comptabilité automatisée',
    'Export comptable',
    'Rapports avancés',
    'Support prioritaire',
  ];

  const currentTier = user?.subscriptionTier;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Choisissez votre offre
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Retour
        </Button>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        {/* Toggle Mensuel/Annuel */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, gap: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: isYearly ? 400 : 600,
              color: isYearly ? '#6B7280' : '#374151',
            }}
          >
            Paiement Mensuel
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isYearly}
                onChange={(e) => setIsYearly(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#10B981',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#10B981',
                  },
                }}
              />
            }
            label=""
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: isYearly ? 600 : 400,
                color: isYearly ? '#374151' : '#6B7280',
              }}
            >
              Paiement Annuel
            </Typography>
            {isYearly && (
              <Chip
                label="Économisez 17%"
                size="small"
                sx={{
                  bgcolor: '#10B981',
                  color: 'white',
                  fontWeight: 600,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.8 },
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Cartes de tarification */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Offre Essentiel */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: currentTier === 'ESSENTIEL' ? '3px solid #F59E0B' : '2px solid #E5E7EB',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                },
              }}
            >
              {currentTier === 'ESSENTIEL' && (
                <Chip
                  label="Votre offre actuelle"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: '#F59E0B',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                  Essentiel
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  Pour démarrer votre activité
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h2" sx={{ fontWeight: 900, color: '#374151' }}>
                    {isYearly ? pricing.ESSENTIEL.yearly : pricing.ESSENTIEL.monthly}€
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#6B7280' }}>
                    HT / {isYearly ? 'an' : 'mois'}
                  </Typography>
                </Box>

                {isYearly && (
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mt: 1 }}>
                    Soit 10,75€/mois au lieu de 12,9€
                  </Typography>
                )}
              </Box>

              <List sx={{ flex: 1, mb: 3 }}>
                {essentielFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 1, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleOutline sx={{ color: '#10B981' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        color: '#374151',
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant={currentTier === 'ESSENTIEL' ? 'outlined' : 'contained'}
                size="large"
                onClick={() => handleSubscribe('ESSENTIEL')}
                disabled={isLoading || currentTier === 'ESSENTIEL'}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: currentTier === 'ESSENTIEL' ? 'transparent' : '#374151',
                  '&:hover': {
                    bgcolor: currentTier === 'ESSENTIEL' ? 'transparent' : '#1F2937',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : currentTier === 'ESSENTIEL' ? (
                  'Offre actuelle'
                ) : (
                  'Choisir Essentiel'
                )}
              </Button>
            </Paper>
          </Grid>

          {/* Offre Premium */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(145deg, #FEF3C7 0%, #FDE68A 100%)',
                border: '3px solid #F59E0B',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 32px rgba(245,158,11,0.3)',
                },
              }}
            >
              {/* Badge Premium */}
              <Chip
                icon={<StarOutline sx={{ color: 'white !important' }} />}
                label="RECOMMANDÉ"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: '#F59E0B',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              />

              {currentTier === 'PREMIUM' && (
                <Chip
                  label="Votre offre actuelle"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    bgcolor: '#10B981',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}

              <Box sx={{ textAlign: 'center', mb: 3, mt: currentTier === 'PREMIUM' ? 2 : 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400E', mb: 1 }}>
                  Premium
                </Typography>
                <Typography variant="body2" sx={{ color: '#92400E', mb: 2 }}>
                  Pour professionnaliser votre activité
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h2" sx={{ fontWeight: 900, color: '#F59E0B' }}>
                    {isYearly ? pricing.PREMIUM.yearly : pricing.PREMIUM.monthly}€
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#92400E' }}>
                    HT / {isYearly ? 'an' : 'mois'}
                  </Typography>
                </Box>

                {isYearly && (
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mt: 1 }}>
                    Soit 16,58€/mois au lieu de 19,9€
                  </Typography>
                )}
              </Box>

              <List sx={{ flex: 1, mb: 3 }}>
                {premiumFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 1, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {index === 0 ? (
                        <ElectricBoltOutlined sx={{ color: '#F59E0B' }} />
                      ) : (
                        <CheckCircleOutline sx={{ color: '#10B981' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        color: '#92400E',
                        fontWeight: index > 0 && index < 4 ? 600 : 400,
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant={currentTier === 'PREMIUM' ? 'outlined' : 'contained'}
                size="large"
                onClick={() => handleSubscribe('PREMIUM')}
                disabled={isLoading || currentTier === 'PREMIUM'}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 700,
                  bgcolor: currentTier === 'PREMIUM' ? 'transparent' : '#F59E0B',
                  borderColor: '#F59E0B',
                  color: currentTier === 'PREMIUM' ? '#F59E0B' : 'white',
                  '&:hover': {
                    bgcolor: currentTier === 'PREMIUM' ? 'transparent' : '#D97706',
                    borderColor: '#D97706',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : currentTier === 'PREMIUM' ? (
                  'Offre actuelle'
                ) : (
                  'Choisir Premium'
                )}
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Informations supplémentaires */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#374151', fontWeight: 600, mb: 2 }}>
            🎁 Abonnement sans engagement • Annulation à tout moment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paiement sécurisé par Stripe • Support client réactif
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Des questions ? Contactez-nous à support@sersal.fr
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingPage;
